/**
 * Canonical observation rows for Phase 1 in-process inspection (SU/QCD/F/07 style).
 * Specs resolve from part.parameter_specs by alias match.
 */

export function timeToMinutes(t) {
  if (!t || typeof t !== 'string') return 0;
  const [h, m] = t.split(':').map((x) => parseInt(x, 10));
  if (Number.isNaN(h)) return 0;
  return h * 60 + (Number.isNaN(m) ? 0 : m);
}

export function minutesToDisplayHourLabel(totalMinutes) {
  const m = ((totalMinutes % (24 * 60)) + 24 * 60) % (24 * 60);
  const h24 = Math.floor(m / 60);
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return String(h12);
}

/** Same effective working-hours count as hourly production (10 / 7 / rounded calendar span). */
export function computeWorkingHoursFromShift(shiftStart, shiftEnd) {
  const startM = timeToMinutes(shiftStart);
  const endM = timeToMinutes(shiftEnd);
  const durationMinutes = endM >= startM ? endM - startM : 24 * 60 - startM + endM;
  const durationHours = durationMinutes / 60;
  if (durationHours >= 10.5) return 10; // 12 hr shift: 10 hrs work, 2 hrs misc
  if (durationHours >= 7 && durationHours <= 8) return 7; // 8 hr shift: 7 hrs work, 1 hr misc
  return Math.max(0, Math.round(durationHours));
}

export function buildHourlySlots(shiftStart, shiftEnd) {
  const startM = timeToMinutes(shiftStart);
  const hoursToRender = computeWorkingHoursFromShift(shiftStart, shiftEnd);
  const out = [];
  let cur = startM;
  for (let i = 0; i < hoursToRender; i += 1) {
    const next = (cur + 60) % (24 * 60);
    out.push({
      key: `h${String(cur).padStart(4, '0')}`,
      startMinutes: cur,
      endMinutes: next,
      label: `${minutesToDisplayHourLabel(cur)} to ${minutesToDisplayHourLabel(next)}`,
    });
    cur = next;
  }
  return out;
}

/**
 * Two-hour observation columns from shift start, spanning the same effective working hours as hourly production.
 * Last block may be shorter than 2h when working hours are odd (e.g. 7h → 2+2+2+1).
 */
export function buildObservationSlots(shiftStart, shiftEnd) {
  const startM = timeToMinutes(shiftStart);
  const workingHours = computeWorkingHoursFromShift(shiftStart, shiftEnd);
  const out = [];
  let cur = startM;
  let remaining = workingHours;
  while (remaining > 0) {
    const thisSlotHours = Math.min(2, remaining);
    const next = (cur + thisSlotHours * 60) % (24 * 60);
    out.push({
      key: `o${String(cur).padStart(4, '0')}_${thisSlotHours}`,
      startMinutes: cur,
      endMinutes: next,
      label: `${minutesToDisplayHourLabel(cur)} to ${minutesToDisplayHourLabel(next)}`,
    });
    cur = next;
    remaining -= thisSlotHours;
  }
  return out;
}

/**
 * Split effective working time into two contiguous halves (same working-hours logic as hourly production).
 * Labels use 12-hour clock numbers (e.g. 8–1 and 1–6 for a 10h day starting 8:00).
 */
export function buildTwoHalfWorkingIntervals(shiftStart, shiftEnd) {
  const startM = timeToMinutes(shiftStart);
  const wh = computeWorkingHoursFromShift(shiftStart, shiftEnd);
  const totalMin = wh * 60;
  if (totalMin <= 0) return [];
  const half = totalMin / 2;
  const midM = (startM + half) % (24 * 60);
  const endM = (startM + totalMin) % (24 * 60);
  const label = (a, b) => `${minutesToDisplayHourLabel(a)} – ${minutesToDisplayHourLabel(b)}`;
  return [
    { key: "interval_0", startMinutes: startM, endMinutes: midM, label: label(startM, midM) },
    { key: "interval_1", startMinutes: midM, endMinutes: endM, label: label(midM, endM) },
  ];
}

export const CANONICAL_ROWS = [
  {
    id: 'face_one_side',
    label: 'Face One Side',
    aliases: ['face one side', 'face side', 'face'],
    defaultSpecification: '32.60 ± 0.20',
    rowType: 'numeric_tol',
  },
  {
    id: 'od',
    label: 'OD',
    aliases: ['od', 'outer diameter'],
    defaultSpecification: '32.60 ± 0.20',
    rowType: 'numeric_tol',
  },
  {
    id: 'od_radius',
    label: 'OD Radius',
    aliases: ['od radius', 'radius od'],
    defaultSpecification: '0.80 ± 0.05',
    rowType: 'numeric_tol',
  },
  {
    id: 'bore',
    label: 'Bore',
    aliases: ['bore', 'id'],
    defaultSpecification: '16.00 ± 0.05',
    rowType: 'numeric_tol',
  },
  {
    id: 'bore_radius',
    label: 'Bore Radius',
    aliases: ['bore radius', 'radius bore'],
    defaultSpecification: '0.40 ± 0.05',
    rowType: 'numeric_tol',
  },
  {
    id: 'three_point_ecc',
    label: '3 Point / Ecc.',
    aliases: ['3 point', 'ecc', 'eccentricity', 'three point'],
    defaultSpecification: '0.05',
    rowType: 'multi5',
  },
  {
    id: 'visual',
    label: 'Visual',
    aliases: ['visual'],
    defaultSpecification: 'OK',
    rowType: 'dropdown',
    options: ['OK', 'NOT OK'],
  },
  {
    id: 'traceability',
    label: 'Traceability',
    aliases: ['traceability'],
    defaultSpecification: 'Available',
    rowType: 'dropdown',
    options: ['Available', 'Not Available'],
  },
  {
    id: 'result',
    label: 'Result',
    aliases: ['result'],
    defaultSpecification: 'OK',
    rowType: 'dropdown',
    options: ['OK', 'NOT OK', 'ADJUST'],
  },
];

export function normalizeKey(s) {
  return (s || '').toLowerCase().replace(/\s+/g, ' ').trim();
}

/** Match part.parameter_specs to a canonical row; return specification string. */
export function resolveSpecificationForRow(part, row) {
  if (!part?.parameter_specs?.length) return '';
  const aliases = (row.aliases || []).map(normalizeKey);
  for (const spec of part.parameter_specs) {
    const p = normalizeKey(spec.parameter || '');
    if (!p) continue;
    for (const a of aliases) {
      if (!a) continue;
      if (p === a || p.includes(a) || a.includes(p)) {
        return (spec.specification || '').trim();
      }
    }
  }
  return '';
}

export function buildEmptyObservation() {
  const obs = {};
  for (const row of CANONICAL_ROWS) {
    obs[row.id] = {};
  }
  return obs;
}

export function buildEmptyHourlyProduction() {
  return {};
}

export function defaultFormData() {
  return {
    header: {
      date: new Date().toISOString().split('T')[0],
      /** Calendar shift (matches production run): DAY | NIGHT */
      shift: 'DAY',
      machine_id: '',
      part_id: '',
      batch_no: '',
      heat_no: '',
      cycle_time: '',
      operator_id: '',
      drawing_no: '',
    },
    shift: {
      shift_start: '08:00',
      shift_end: '20:00',
    },
    summary_quantities: {
      target_quantity: '',
      total_produced: '',
      total_rejected: '',
      ok_quantity: '',
    },
    hourly_production: buildEmptyHourlyProduction(),
    observation: buildEmptyObservation(),
    /** multi5 params: rowId -> { interval_0: string[], interval_1: string[] } */
    multi5_observation: {},
  };
}

export function mergeMulti5Observation(base, loaded) {
  const out = { ...(base || {}) };
  for (const [rowId, rowData] of Object.entries(loaded || {})) {
    if (!rowData || typeof rowData !== "object") continue;
    const merged = { ...(out[rowId] || {}), ...rowData };
    for (const k of ["interval_0", "interval_1"]) {
      const arr = merged[k];
      if (Array.isArray(arr)) merged[k] = arr.map((x) => (x == null ? "" : String(x)));
    }
    out[rowId] = merged;
  }
  return out;
}

export function mergeObservation(baseObs, loadedObs) {
  const out = {};
  const rowIds = new Set([...Object.keys(baseObs || {}), ...Object.keys(loadedObs || {})]);
  for (const rowId of rowIds) {
    const baseRow = baseObs[rowId] || {};
    const loadedRow = loadedObs[rowId] || {};
    const merged = { ...baseRow, ...loadedRow };
    for (const k of Object.keys(merged)) {
      const v = merged[k];
      if (v !== null && v !== undefined && typeof v !== 'object') {
        merged[k] = String(v);
      }
    }
    out[rowId] = merged;
  }
  return out;
}

export function mergeHourlyProduction(baseHourly, loadedHourly) {
  const out = { ...(baseHourly || {}) };
  for (const [key, raw] of Object.entries(loadedHourly || {})) {
    if (!raw || typeof raw !== 'object') continue;
    out[key] = {
      target_qty:
        raw.target_qty !== undefined && raw.target_qty !== null ? String(raw.target_qty) : '',
      produced_qty:
        raw.produced_qty !== undefined && raw.produced_qty !== null ? String(raw.produced_qty) : '',
      rejected_qty:
        raw.rejected_qty !== undefined && raw.rejected_qty !== null ? String(raw.rejected_qty) : '',
      remarks: raw.remarks !== undefined && raw.remarks !== null ? String(raw.remarks) : '',
    };
  }
  return out;
}

/** Merge server form_data with defaults so new rows/slots do not break older drafts. */
export function mergeFormData(loaded) {
  const base = defaultFormData();
  if (!loaded || typeof loaded !== 'object') return base;
  return {
    header: { ...base.header, ...(loaded.header || {}) },
    shift: { ...base.shift, ...(loaded.shift || {}) },
    summary_quantities: { ...base.summary_quantities, ...(loaded.summary_quantities || {}) },
    hourly_production: mergeHourlyProduction(base.hourly_production, loaded.hourly_production || {}),
    observation: mergeObservation(base.observation, loaded.observation || {}),
    multi5_observation: mergeMulti5Observation(base.multi5_observation, loaded.multi5_observation || {}),
  };
}
